import { FC, MouseEventHandler, ReactNode, useLayoutEffect, useRef, useState } from 'react'
import ButtonBase from '@mui/material/ButtonBase'
import CircularProgress from '@mui/material/CircularProgress'
import Box from '@mui/material/Box'
import { keyframes, type Theme } from '@mui/material/styles'
import CheckIcon from '@mui/icons-material/Check'
import { getNf, nf, type NfTokens } from '../nfTokens'

export interface NfPillProps {
  children: ReactNode
  variant?: 'primary' | 'secondary' | 'ghost'
  type?: 'button' | 'submit'
  onClick?: MouseEventHandler<HTMLButtonElement>
  disabled?: boolean
  loading?: boolean
  /** L'action a abouti : l'icône devient une coche. */
  done?: boolean
  icon?: ReactNode
  'data-testid'?: string
}

const variantSx = (variant: NonNullable<NfPillProps['variant']>, t: NfTokens) => {
  if (variant === 'primary') {
    return {
      bgcolor: t.green,
      color: t.onGreen,
      border: '1px solid transparent',
      '&:hover': { bgcolor: t.darkGreenHover },
    }
  }
  if (variant === 'secondary') {
    return {
      bgcolor: t.surface,
      color: t.ink2,
      border: `1px solid ${t.line}`,
      '&:hover': { bgcolor: t.line2 },
    }
  }
  return {
    bgcolor: 'transparent',
    color: t.muted,
    border: '1px solid transparent',
    '&:hover': { bgcolor: t.line2 },
  }
}

const spinnerIn = keyframes`
  from { opacity: 0; transform: scale(.4); }
  to { opacity: 1; transform: none; }
`

const checkPop = keyframes`
  from { opacity: 0; transform: scale(.4); }
  60% { opacity: 1; transform: scale(1.15); }
  to { opacity: 1; transform: none; }
`

const slotLayerSx = {
  position: 'absolute',
  inset: 0,
  display: 'grid',
  placeItems: 'center',
} as const

const enterSx = (enter: ReturnType<typeof keyframes>, timing: string) => ({
  animation: `${enter} ${timing} both`,
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
})

/**
 * Bouton pilule reproduisant "NfPill" de la maquette : hauteur 36, rayon 99,
 * 3 variantes (primary/secondary/ghost). Toujours type="button" par défaut
 * (jamais "submit") : la modale déclenche l'envoi via formik.submitForm()
 * pour éviter qu'un bouton change de type juste après un clic (piège React
 * classique qui peut faire soumettre le formulaire malgré lui).
 */
export const NfPill: FC<NfPillProps> = ({
  children,
  variant = 'secondary',
  type = 'button',
  onClick,
  disabled,
  loading,
  done,
  icon,
  'data-testid': dataTestId,
}) => {
  // Pendant l'action et après, le bouton est inactif mais ne doit pas paraître éteint.
  const busy = Boolean(loading || done)

  // Largeur du contenu, mesurée avant l'affichage : quand le libellé change
  // (« Créer le dossier » → « Création… »), le bouton glisse vers la nouvelle
  // largeur au lieu de sauter. Rien tant qu'aucune mesure n'existe (jsdom).
  const contentRef = useRef<HTMLSpanElement>(null)
  const [contentWidth, setContentWidth] = useState<number>()
  useLayoutEffect(() => {
    const el = contentRef.current
    if (!el) return undefined
    const measure = () => {
      // Arrondi au-dessus : un demi-pixel perdu rognerait la dernière lettre.
      const width = Math.ceil(el.getBoundingClientRect().width)
      if (width > 0) setContentWidth(width)
    }
    measure()
    if (typeof ResizeObserver === 'undefined') return undefined
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [])
  const spinner = (
    <CircularProgress
      size={12}
      thickness={5}
      sx={(theme) => ({
        color: variant === 'primary' ? getNf(theme).onGreen : getNf(theme).muted,
      })}
    />
  )
  return (
    <ButtonBase
      type={type}
      onClick={onClick}
      disabled={disabled}
      data-testid={dataTestId}
      sx={(theme: Theme) => {
        const t = getNf(theme)
        return {
          ...variantSx(variant, t),
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: `${nf.fieldHeight}px`,
          px: 2.25,
          borderRadius: `${nf.radiusPill}px`,
          fontFamily: 'inherit',
          fontSize: 11.5,
          fontWeight: 600,
          whiteSpace: 'nowrap',
          opacity: disabled && !busy ? 0.5 : 1,
          transition: 'background-color .14s ease',
        }
      }}
    >
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          justifyContent: 'center',
          overflow: 'hidden',
          width: contentWidth === undefined ? undefined : `${contentWidth}px`,
          transition: 'width .26s cubic-bezier(.2, .8, .2, 1)',
          '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
        }}
      >
        <Box
          component="span"
          ref={contentRef}
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}
        >
          {icon ? (
            // Icône, spinner et coche partagent la même place et passent de l'un à l'autre.
            <Box component="span" sx={{ position: 'relative', width: 14, height: 14, flexShrink: 0 }}>
              <Box
                component="span"
                sx={{
                  ...slotLayerSx,
                  opacity: busy ? 0 : 1,
                  transform: busy ? 'rotate(90deg) scale(.4)' : 'none',
                  transition: 'opacity .18s ease, transform .26s cubic-bezier(.2, .8, .2, 1)',
                  '@media (prefers-reduced-motion: reduce)': { transition: 'none', transform: 'none' },
                }}
              >
                {icon}
              </Box>
              {loading && !done && (
                <Box component="span" sx={{ ...slotLayerSx, ...enterSx(spinnerIn, '.22s ease .08s') }}>
                  {spinner}
                </Box>
              )}
              {done && (
                <Box
                  component="span"
                  data-testid="nf-pill-done"
                  sx={{ ...slotLayerSx, ...enterSx(checkPop, '.32s cubic-bezier(.34, 1.56, .64, 1)') }}
                >
                  <CheckIcon sx={{ fontSize: 15 }} />
                </Box>
              )}
            </Box>
          ) : loading ? (
            spinner
          ) : null}
          {children}
        </Box>
      </Box>
    </ButtonBase>
  )
}

export default NfPill
